import type { PaginatedResponse, UserFilterParams } from '@/types/api';
import type {
    CreateObjectiveCommentRequest,
    CreateObjectiveRequest,
    EmployeesWithoutObjectivesParams,
    EmployeeWithoutObjective,
    KeyResultProgressRequest,
    ObjectiveFilteredEmployee,
    Objective,
    ObjectiveComment,
    ObjectiveOrDraft,
    ObjectiveDraftRequest,
    ObjectiveDraftResponse,
    ObjectiveParticipantAttribute,
    ObjectiveWindow,
    PillarSummary,
    ResolvedParticipantAttributes,
    UpdateObjectiveRequest,
} from '@/types/objectives';

import { api } from '@/api/api';

export interface UpdateKeyResultProgressParams {
    objectiveId: string;
    keyResultId: string;
    request: KeyResultProgressRequest;
}

/**
 * Builds the `objectivePeriodId`/`from`/`to`/`pillarId`/`employeeId` query params, omitting keys that are
 * undefined. Returns `undefined` rather than an empty object so axios omits `params` entirely.
 */
const windowParams = (objectiveWindow?: ObjectiveWindow): Record<string, string> | undefined => {
    if (!objectiveWindow) {
        return undefined;
    }

    const params: Record<string, string> = {};
    if (objectiveWindow.objectivePeriodId) {
        params.objectivePeriodId = objectiveWindow.objectivePeriodId;
    }
    if (objectiveWindow.from) {
        params.from = objectiveWindow.from;
    }
    if (objectiveWindow.to) {
        params.to = objectiveWindow.to;
    }
    if (objectiveWindow.pillarId) {
        params.pillarId = objectiveWindow.pillarId;
    }
    if (objectiveWindow.employeeId) {
        params.employeeId = objectiveWindow.employeeId;
    }

    return Object.keys(params).length > 0 ? params : undefined;
};

export default {
    async getObjectives(objectiveWindow?: ObjectiveWindow): Promise<Objective[]> {
        const response = await api.get('/Objective', { params: windowParams(objectiveWindow) });
        return response.data;
    },

    /** The published objectives, followed by the drafts the caller sees. */
    async getObjectivesWithDrafts(objectiveWindow?: ObjectiveWindow): Promise<ObjectiveOrDraft[]> {
        const response = await api.get('/Objective', {
            params: { ...windowParams(objectiveWindow), includeDrafts: true },
        });
        return response.data;
    },

    async getObjectiveById(id: string): Promise<ObjectiveOrDraft> {
        const response = await api.get(`/Objective/${id}`);
        return response.data;
    },

    async createObjective(request: CreateObjectiveRequest): Promise<ObjectiveOrDraft> {
        const response = await api.post('/Objective', request);
        return response.data;
    },

    async updateObjective(id: string, request: UpdateObjectiveRequest): Promise<ObjectiveOrDraft> {
        const response = await api.put(`/Objective/${id}`, request);
        return response.data;
    },

    async deleteObjective(id: string): Promise<ObjectiveOrDraft> {
        const response = await api.delete(`/Objective/${id}`);
        return response.data;
    },

    async updateKeyResultProgress({
        objectiveId,
        keyResultId,
        request,
    }: UpdateKeyResultProgressParams): Promise<Objective> {
        const response = await api.patch(
            `/Objective/${objectiveId}/KeyResults/${keyResultId}/Progress`,
            request,
        );
        return response.data;
    },

    /**
     * Who the attribute values bring in today: those `objectiveId` already has within the reach they
     * were added under, the others within the caller's.
     */
    async resolveParticipantAttributes(
        attributes: ObjectiveParticipantAttribute[],
        objectiveId?: string,
    ): Promise<ResolvedParticipantAttributes> {
        const response = await api.post('/Objective/ParticipantAttributes/Resolve', {
            attributes,
            objectiveId,
        });
        return response.data;
    },

    /** The managers whose team the reader may choose as participants, as the server reaches them. */
    async getChoosableTeamManagerIds(): Promise<string[]> {
        const response = await api.get('/Objective/ParticipantAttributes/TeamManagers');
        return response.data;
    },

    /**
     * The pillar summaries of the whole company's objectives, not the reader's: the only reading of
     * the pillars, whatever the screen. It takes no objectives permission.
     */
    async getCompanyPillarSummaries(objectiveWindow?: ObjectiveWindow): Promise<PillarSummary[]> {
        const response = await api.get('/employee-portal/pillar-summaries', {
            params: windowParams(objectiveWindow),
        });
        return response.data;
    },

    /** One pillar as `getCompanyPillarSummaries` reads it: the whole company's objectives. */
    async getCompanyPillarSummary(
        pillarId: string,
        objectiveWindow?: ObjectiveWindow,
    ): Promise<PillarSummary> {
        const response = await api.get(`/employee-portal/pillar-summaries/${pillarId}`, {
            params: windowParams(objectiveWindow),
        });
        return response.data;
    },

    async getFilteredEmployees(params: UserFilterParams): Promise<ObjectiveFilteredEmployee[]> {
        const response = await api.get('/Objective/FilteredEmployees', { params });
        return response.data;
    },

    async getEmployeesWithoutObjectives(
        params: EmployeesWithoutObjectivesParams,
    ): Promise<PaginatedResponse<EmployeeWithoutObjective>> {
        const response = await api.get('/Objective/EmployeesWithoutObjectives', { params });
        return response.data;
    },

    async getObjectiveComments(objectiveId: string): Promise<ObjectiveComment[]> {
        const response = await api.get(`/Objective/${objectiveId}/Comments`);
        return response.data;
    },

    async createObjectiveComment(
        objectiveId: string,
        request: CreateObjectiveCommentRequest,
    ): Promise<ObjectiveComment> {
        const response = await api.post(`/Objective/${objectiveId}/Comments`, request);
        return response.data;
    },

    /**
     * Queues an AI draft of an objective. Only the acknowledgement comes back here: the draft is
     * pushed over the AI hub under `request.correlationId` (see `useGenerateObjectiveDraft`).
     */
    async generateObjectiveDraft(request: ObjectiveDraftRequest): Promise<ObjectiveDraftResponse> {
        const response = await api.post('/Objective/Ai/Draft', request);
        return response.data;
    },
};
